import Select from '@ben/orion/deprecated-select';
import { VisuallyHidden } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { TypographyVariant } from '../../utils/theme';
import { IconName } from '../icon/icon';
import { TablePaginationIcon } from '../table/components/table-pagination-icon';
import Typography from '../typography/typography';

interface PaginationControlsProps {
  currentPageIndex: number;
  totalPages: number;
  totalItems: number;
  fromItems: number;
  toItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  hidePageSizeDropdown?: boolean;
  isNarrow?: boolean;
  showPageSizeOptions?: { key: string; label: string }[];
}

export const PaginationControls = ({
  currentPageIndex,
  totalPages,
  totalItems,
  fromItems,
  toItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  hidePageSizeDropdown = true,
  isNarrow = false,
  showPageSizeOptions = [
    { key: '10', label: '10' },
    { key: '25', label: '25' },
    { key: '50', label: '50' },
  ],
}: PaginationControlsProps) => {
  const { t } = useTranslation();
  const pageNumber = currentPageIndex + 1;
  const canPreviousPage = currentPageIndex > 0;
  const canNextPage = currentPageIndex < totalPages - 1;

  // Translation helpers
  const words = {
    showing: t('words.showing', { defaultValue: 'Showing' }),
    to: t('words.to', { defaultValue: 'to' }),
    of: t('words.of', { defaultValue: 'of' }),
    page: t('words.page', { defaultValue: 'Page' }),
    rows:
      totalItems === 1
        ? t('words.row', { defaultValue: 'row' })
        : t('words.rows', { defaultValue: 'rows' }),
    rowsPerPage: t('words.rowsPerPage', { defaultValue: 'Rows per page' }),
  };

  const handleFirstPage = () => {
    if (canPreviousPage) {
      onPageChange(0);
    }
  };

  const handlePreviousPage = () => {
    if (canPreviousPage) {
      onPageChange(currentPageIndex - 1);
    }
  };

  const handleNextPage = () => {
    if (canNextPage) {
      onPageChange(currentPageIndex + 1);
    }
  };

  const handleLastPage = () => {
    if (canNextPage) {
      onPageChange(totalPages - 1);
    }
  };

  return (
    <Wrapper>
      <Typography variant={TypographyVariant.bodyMd1}>
        {!isNarrow && words.showing} <strong>{fromItems}</strong> {words.to}{' '}
        <strong>{toItems}</strong> {words.of} <strong>{totalItems}</strong>{' '}
        {words.rows}
      </Typography>
      <Actions>
        {!hidePageSizeDropdown && onPageSizeChange && (
          <>
            <Typography variant={TypographyVariant.bodyMd1}>
              {words.rowsPerPage}:
            </Typography>
            <StyledSelect
              name={words.rowsPerPage}
              showLabel={false}
              value={String(pageSize)}
              options={showPageSizeOptions}
              onChange={(pageSize) => {
                onPageSizeChange(Number(pageSize));
              }}
            />
          </>
        )}
      </Actions>
      <Actions>
        <IconGroup>
          <TablePaginationIcon
            icon={IconName.Backward}
            title={t('phrases.firstPage', { defaultValue: 'First page' })}
            onClick={handleFirstPage}
            disabled={!canPreviousPage}
          />
          <TablePaginationIcon
            icon={IconName.ArrowsLeft}
            title={t('phrases.previousPage', { defaultValue: 'Previous page' })}
            onClick={handlePreviousPage}
            disabled={!canPreviousPage}
          />
        </IconGroup>
        <Typography variant={TypographyVariant.bodyMd1}>
          {words.page} <strong>{pageNumber}</strong> {words.of}{' '}
          <strong>{totalPages}</strong>
        </Typography>
        <IconGroup>
          <TablePaginationIcon
            icon={IconName.ArrowsRight}
            title={t('phrases.nextPage', { defaultValue: 'Next page' })}
            onClick={handleNextPage}
            disabled={!canNextPage}
          />
          <TablePaginationIcon
            icon={IconName.Forward}
            title={t('phrases.lastPage', { defaultValue: 'Last page' })}
            onClick={handleLastPage}
            disabled={!canNextPage}
          />
        </IconGroup>
      </Actions>
      <VisuallyHidden aria-live="assertive">
        {totalItems === 1
          ? t('phrases.oneResultFound', { defaultValue: '1 result found' })
          : t('phrases.multipleResultsFound', {
              number: totalItems,
              defaultValue: `${totalItems} results found`,
            })}
      </VisuallyHidden>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  ${({ theme }) => css`
    position: sticky; // Sticky when table scrolls horizontally
    left: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.spacing(1.5, 2)};

    strong {
      font-weight: 500;
    }

    @media (max-width: ${theme.breakpoints.sm}) {
      flex-direction: column;
      align-items: flex-start;
      gap: ${theme.spacing(1)};
    }
  `}
`;

const Actions = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(1.5)};
    white-space: nowrap;
  `}
`;

const IconGroup = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledSelect = styled(Select)`
  ${({ theme }) => css`
    width: ${theme.spacing(8.5)};
    height: ${theme.spacing(4)};
    min-height: ${theme.spacing(4)};
    padding: ${theme.spacing(0, 1.5)} !important;
  `}
`;
